import React, { useEffect, useState } from 'react';
import { Link, RouteComponentProps, withRouter } from 'react-router-dom';
import {
  Box,
  Button,
  Card,
  CardActions,
  CardContent,
  CardMedia,
  Typography,
} from '@mui/material';
import Chip from '@mui/material/Chip';
import Cookies from 'universal-cookie';

import {
  BookingService,
  Company,
  UserAdminTag,
  UserPIIRemovalService,
  UserService,
  UserState,
} from 'us-web-services';
import CardStyles from '../../styles/CardStyles';
import HostsService from '../../util/HostsService';
import useAuthentication from '../../store/useAuthentication';
import { StorageNames } from '../../constants';
import DateUtil from '../../util/DateUtil';

interface Props extends RouteComponentProps {
  lookupUser: UserState;
  userAdminTags: UserAdminTag[];
  userCompany?: Company;
}

function UserCard(props: Props) {
  const { history, lookupUser, userAdminTags, userCompany = {} } = props;

  const [authenticationState] = useAuthentication();
  const { classes } = CardStyles();
  const [duplicatePhoneCount, setDuplicatePhoneCount] = useState(0);
  const [completedBookings, setCompletedBookings] = useState(0);
  const [acceptedBookings, setAcceptedBookings] = useState(0);
  const [pendingBookings, setPendingBookings] = useState(0);
  const role = lookupUser.user.parent ? 'parent' : 'sitter';
  const [userPIIRemovalDate, setUserPIIRemovalDate] = useState(null);

  const setBookingCount = (type, count) => {
    switch (type) {
      case 'completed':
        setCompletedBookings(count);
        break;
      case 'accepted':
        setAcceptedBookings(count);
        break;
      case 'pending':
        setPendingBookings(count);
        break;
      default:
        break;
    }
  };

  const getProfileUrl = () => {
    const base = `https://${HostsService.getAppHost()}/app`;

    return `${base}/${role}/${lookupUser.user.id}`;
  };

  const editUser = () => {
    history.push(`/users/edit/${lookupUser.user.id}`);
  };

  const masquerade = () => {
    const host = HostsService.getAppHost();
    const cookies = new Cookies();
    const cookieValue = {
      adminToken: authenticationState.authentication.token,
      host,
      user: lookupUser.user,
      adminUserId: authenticationState.authentication.user.id,
    };

    // Extract base domain for cookie access across subdomains
    // e.g., "www.urbansitter.com" -> ".urbansitter.com"
    const domainParts = host.split('.');
    const domain = domainParts.length > 2
      ? `.${domainParts.slice(-2).join('.')}`
      : `.${host}`;

    cookies.set(StorageNames.US_MASQUERADE, cookieValue, {
      domain,
      path: '/',
    });

    window.open(`https://${cookieValue.host}`, '_blank');
  };

  const isAgency = () => !lookupUser.user.parent && !lookupUser.user.sitter;

  useEffect(() => {
    const adminUserId = authenticationState.authentication.user.id;

    const getBookingCounts = async () => {
      const bookingTypes = ['completed', 'accepted', 'pending'];
      const roleId = `${role}Id`; // parentId or sitterId depending on user role

      const bookingPromises = bookingTypes.map(async filterType => {
        const params = {
          filterType,
          userId: adminUserId,
          minimalProperties: true,
        };

        params[roleId] = lookupUser.user.id;

        const response = await BookingService.getByFilter({ params });

        setBookingCount(filterType, response.data.meta.size);
      });

      await Promise.all(bookingPromises);
    };

    const getDuplicatePhoneCount = async () => {
      const config = {
        params: {
          phone: lookupUser.profile.phone,
          limit: 1,
        },
      };
      const response = (await UserService.getByFilter(config)).data;

      setDuplicatePhoneCount(response.meta.size);
    };

    const getUserPIIRemovalDate = async () => {
      UserPIIRemovalService.findById(lookupUser.user.id).then(response =>
        setUserPIIRemovalDate(response?.data),
      );
    };

    getBookingCounts();

    if (lookupUser.profile.phone) {
      getDuplicatePhoneCount();
    } else {
      setDuplicatePhoneCount(0);
    }

    if (!lookupUser.user.active) {
      getUserPIIRemovalDate();
    }
  }, [lookupUser.user.id, lookupUser.profile.phone, role, authenticationState.authentication.user.id]);

  return (
    <Card className={classes.card}>
      <div className={classes.photo}>
        <CardMedia
          image={lookupUser.user.pictureUrl}
          title={`${lookupUser.user.name}'s photo`}
          component='img'
        />
      </div>
      <div className={classes.detailsMiddle}>
        <CardContent className={classes.content}>
          <Typography variant='h6' sx={{ fontWeight: 600, mb: 0.5 }}>
            <a
              href={getProfileUrl()}
              className={lookupUser.user.active ? '' : classes.error}
              target='_blank'
              rel='noopener noreferrer'
              style={{ color: '#1976d2' }}
            >
              {lookupUser.user.firstName} {lookupUser.user.lastName}
            </a>
            <Typography component='span' sx={{ ml: 1, color: 'text.secondary', fontSize: '0.875rem' }}>
              ID: {lookupUser.user.id}
            </Typography>
          </Typography>
          {!lookupUser.user.active && (
            <Chip
              label='Deactivated'
              size='small'
              color='error'
              sx={{ mb: 1 }}
            />
          )}
          <Box sx={{ mt: 1.5 }}>
            <div className={classes.label}>Email</div>
            <div className={classes.value}>{lookupUser.user.email}</div>

            <div className={classes.label}>Location</div>
            <div className={classes.value}>
              {lookupUser.profile.address.city ? `${lookupUser.profile.address.city}, ` : ''}
              {lookupUser.profile.address.state || 'N/A'}
            </div>

            <div className={classes.label}>Phone</div>
            <div className={classes.value}>
              {lookupUser.profile.phone ? (
                <Link to={`/users/lookup?search=${encodeURIComponent(lookupUser.profile.phone)}`}>
                  {lookupUser.profile.phone}
                </Link>
              ) : (
                'N/A'
              )}
              {duplicatePhoneCount > 1 && (
                <Chip
                  label={`${duplicatePhoneCount} duplicates`}
                  size='small'
                  color='warning'
                  sx={{ ml: 1 }}
                />
              )}
            </div>

            <div className={classes.label}>Role</div>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 1 }}>
              {lookupUser.user.parent && (
                <div className={classes.roleCard}>
                  <span className={classes.roleIcon}>👪</span>
                  <span className={classes.roleLabel}>Parent</span>
                </div>
              )}
              {lookupUser.user.sitter && (
                <div className={classes.roleCard}>
                  <span className={classes.roleIcon}>🧑‍🍼</span>
                  <span className={classes.roleLabel}>Sitter</span>
                </div>
              )}
              {lookupUser.user.superUser && (
                <div className={classes.roleCardAdmin}>
                  <span className={classes.roleIcon}>⭐</span>
                  <span className={classes.roleLabel}>Super User</span>
                </div>
              )}
              {isAgency() && (
                <div className={classes.roleCard}>
                  <span className={classes.roleIcon}>🏢</span>
                  <span className={classes.roleLabel}>Agency</span>
                </div>
              )}
            </Box>
          </Box>
          <CardActions sx={{ px: 0, mt: 1 }}>
            {!isAgency() && (
              <Button
                size='small'
                variant='contained'
                color='primary'
                onClick={masquerade}
                sx={{ textTransform: 'none' }}
              >
                Masquerade
              </Button>
            )}
            <Button
              size='small'
              variant='outlined'
              color='primary'
              onClick={editUser}
              sx={{ textTransform: 'none' }}
            >
              Edit User
            </Button>
          </CardActions>
        </CardContent>
      </div>
      <div className={classes.detailsRight}>
        <CardContent className={classes.content}>
          <div className={classes.label}>Bookings</div>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', mb: 2 }}>
            <Link to={`/users/bookings?id=${lookupUser.user.id}&filterType=completed`} className={classes.statCard}>
              <span className={classes.statValue}>{completedBookings}</span>
              <span className={classes.statLabel}>Completed</span>
            </Link>
            <Link to={`/users/bookings?id=${lookupUser.user.id}&filterType=accepted`} className={classes.statCard}>
              <span className={classes.statValue}>{acceptedBookings}</span>
              <span className={classes.statLabel}>Accepted</span>
            </Link>
            <Link to={`/users/bookings?id=${lookupUser.user.id}&filterType=pending`} className={classes.statCard}>
              <span className={classes.statValue}>{pendingBookings}</span>
              <span className={classes.statLabel}>Pending</span>
            </Link>
          </Box>

          {lookupUser.user.sitter && (
            <>
              <div className={classes.label}>Date of Birth</div>
              <div className={classes.value}>{DateUtil.getReadableDate(lookupUser.profile.dob)}</div>
            </>
          )}

          <div className={classes.label}>Registered</div>
          <div className={classes.value}>{DateUtil.getReadableDateTime(lookupUser.user.created)}</div>

          {lookupUser.profile.userStats && (
            <>
              <div className={classes.label}>Last Active</div>
              <div className={classes.value}>
                {DateUtil.getReadableDateTime(lookupUser.profile.userStats.lastAccessDate)}
              </div>
            </>
          )}

          {userPIIRemovalDate && (
            <>
              <div className={classes.label}>PII Removed</div>
              <div className={classes.value}>
                {DateUtil.getReadableDateTime(userPIIRemovalDate.modified)}
              </div>
            </>
          )}

          <div className={classes.label}>Admin Tags</div>
          <div className={classes.value}>
            {!userAdminTags || !userAdminTags.length ? (
              <Typography variant='body2' color='text.secondary'>None</Typography>
            ) : (
              userAdminTags.map(tag => (
                <Chip
                  key={tag.id}
                  label={tag.tag.code}
                  size='small'
                  color='secondary'
                  sx={{ mr: 0.5, mb: 0.5 }}
                />
              ))
            )}
          </div>

          {userCompany && (
            <>
              <div className={classes.label}>BD Company</div>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', mb: 2 }}>
                <Link to={`/companies/${userCompany.id}`} className={classes.bdCompanyCard}>
                  <span className={classes.bdCompanyName}>{userCompany.name}</span>
                  <span className={classes.statLabel}>View Company</span>
                </Link>
              </Box>
            </>
          )}
        </CardContent>
      </div>
    </Card>
  );
}

export default withRouter(UserCard);
